import React, { forwardRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import * as Yup from "yup";

import { TextField } from "@mui/material";
import { ButtonsWrapper, StyledForm } from "./styled";

import { getRequiredField } from "helpers/formValidations";
import { APARTMENTS_CONFIG, APARTMENTS_PHRASE } from "./config";

import RegularButton from "components/CustomButtons/Button";
import AutocompleteControlled from "components/AutocompleteControlled/AutocompleteControlled";
import {
  fetchNewApartment,
  fetchEditApartment
} from "redux/actions/buildings/apartments/apartmentsActions";

const ApartmentsSchema = Yup.object({
  [APARTMENTS_CONFIG.facility.key]: Yup.object().required(
    getRequiredField(APARTMENTS_CONFIG.facility.label)
  ),
  [APARTMENTS_CONFIG.apartmentNumber.key]: Yup.string().required(
    getRequiredField(APARTMENTS_CONFIG.apartmentNumber.label)
  ),
  [APARTMENTS_CONFIG.buildingTower.key]: Yup.string(),
  [APARTMENTS_CONFIG.floor.key]: Yup.string(),
  [APARTMENTS_CONFIG.releaseDate.key]: Yup.date("")
    .typeError(getRequiredField(APARTMENTS_CONFIG.releaseDate.label))
    .required(getRequiredField(APARTMENTS_CONFIG.releaseDate.label)),
  [APARTMENTS_CONFIG.sector.key]: Yup.object().required(
    getRequiredField(APARTMENTS_CONFIG.sector.label)
  ),
  [APARTMENTS_CONFIG.status.key]: Yup.object().required(
    getRequiredField(APARTMENTS_CONFIG.status.label)
  )
});

export const ApartmentsForm = forwardRef(props => {
  const dispatch = useDispatch();
  const { handleClose } = props;
  const { isAddMode, isDeleteMode, editApartment } = useSelector(
    state => state.apartments
  );

  const { businesses } = useSelector(state => state.apartments);
  let { statuses } = useSelector(state => state.apartments);
  const { facilities } = useSelector(state => state.facilities);

  const facilitiesMemo = useMemo(
    () =>
      facilities?.map(facility => ({
        id: facility.id,
        value: facility.id,
        label: facility.name
      })),
    [facilities]
  );

  const {
    reset,
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(ApartmentsSchema),
    defaultValues: {
      [APARTMENTS_CONFIG.status.key]: "ACTIVO"
    }
  });

  const handleCloseForm = () => {
    handleClose();
    reset();
  };

  const onSubmit = React.useCallback(
    formData => {
      // You can use the validated data to make your API request here
      const body = {
        idFacility: formData[APARTMENTS_CONFIG.facility.key]?.id,
        number: formData[APARTMENTS_CONFIG.apartmentNumber.key],
        tower: formData[APARTMENTS_CONFIG.buildingTower.key],
        flat: formData[APARTMENTS_CONFIG.floor.key],
        registrationDate: dayjs(
          formData[APARTMENTS_CONFIG.releaseDate.key]
        ).format("YYYY-MM-DD"),
        business: formData[APARTMENTS_CONFIG.sector.key]?.id,
        status: formData[APARTMENTS_CONFIG.status.key]?.id
      };

      if (isAddMode) {
        dispatch(fetchNewApartment(body));
      } else {
        dispatch(fetchEditApartment(body, editApartment.id));
      }

      handleCloseForm();
    },
    [isAddMode, editApartment]
  );

  useEffect(() => {
    if (editApartment) {
      setValue(
        APARTMENTS_CONFIG.facility.key,
        facilitiesMemo.find(_ => _.id == editApartment.facility?.id)
      );
      setValue(
        APARTMENTS_CONFIG.apartmentNumber.key,
        editApartment[APARTMENTS_CONFIG.apartmentNumber.key]
      );
      setValue(
        APARTMENTS_CONFIG.buildingTower.key,
        editApartment[APARTMENTS_CONFIG.buildingTower.key]
      );
      setValue(
        APARTMENTS_CONFIG.floor.key,
        editApartment[APARTMENTS_CONFIG.floor.key]
      );
      setValue(
        APARTMENTS_CONFIG.releaseDate.key,
        dayjs(editApartment[APARTMENTS_CONFIG.releaseDate.key]).format(
          "YYYY-MM-DD"
        )
      );
      setValue(
        APARTMENTS_CONFIG.sector.key,
        businesses.find(_ => _.value === editApartment.business)
      );
      if (!statuses.find(obj => obj.value === editApartment.status)) {
        statuses = [...statuses, {
          id: editApartment.status,
          value: editApartment.status,
          label: editApartment.status
        }]
      }

      setValue(
        APARTMENTS_CONFIG.status.key,
        statuses.find(_ => _.value === editApartment.status)
      );

      if (editApartment.status === 'CORTAR') {
        statuses = [...statuses, {
          id: 'DESCONECTADO',
          value: 'DESCONECTADO',
          label: 'DESCONECTADO'
        }]
      }
    }
  }, [editApartment]);

  useEffect(() => {
    if (isAddMode) {
      reset();
    }
  }, [isAddMode]);

  return (
    <>
      <StyledForm component="form" fullWidth onSubmit={handleSubmit(onSubmit)}>
        <AutocompleteControlled
          name={APARTMENTS_CONFIG.facility.key}
          label={APARTMENTS_CONFIG.facility.label}
          options={facilitiesMemo}
          control={control}
        />
        <TextField
          fullWidth
          type="text"
          label={APARTMENTS_CONFIG.buildingTower.label}
          variant="outlined"
          error={!!errors[APARTMENTS_CONFIG.buildingTower.key]}
          helperText={errors[APARTMENTS_CONFIG.buildingTower.key]?.message}
          {...register(APARTMENTS_CONFIG.buildingTower.key)}
          size="small"
        />
        <TextField
          fullWidth
          type="text"
          label={APARTMENTS_CONFIG.floor.label}
          variant="outlined"
          error={!!errors[APARTMENTS_CONFIG.floor.key]}
          helperText={errors[APARTMENTS_CONFIG.floor.key]?.message}
          {...register(APARTMENTS_CONFIG.floor.key)}
          size="small"
        />
        <TextField
          fullWidth
          type="text"
          label={APARTMENTS_CONFIG.apartmentNumber.label}
          variant="outlined"
          error={!!errors[APARTMENTS_CONFIG.apartmentNumber.key]}
          helperText={errors[APARTMENTS_CONFIG.apartmentNumber.key]?.message}
          {...register(APARTMENTS_CONFIG.apartmentNumber.key)}
          size="small"
        />
        <TextField
          fullWidth
          type="date"
          label={APARTMENTS_CONFIG.releaseDate.label}
          variant="outlined"
          error={!!errors[APARTMENTS_CONFIG.releaseDate.key]}
          helperText={errors[APARTMENTS_CONFIG.releaseDate.key]?.message}
          {...register(APARTMENTS_CONFIG.releaseDate.key)}
          InputLabelProps={{
            shrink: true
          }}
          size="small"
        />
        <AutocompleteControlled
          name={APARTMENTS_CONFIG.sector.key}
          label={APARTMENTS_CONFIG.sector.label}
          options={businesses}
          control={control}
        />
        <AutocompleteControlled
          name={APARTMENTS_CONFIG.status.key}
          label={APARTMENTS_CONFIG.status.label}
          options={statuses}
          control={control}
        />
        <ButtonsWrapper>
          <RegularButton
            variant="contained"
            color="danger"
            onClick={handleCloseForm}
          >
            {APARTMENTS_PHRASE.modal.actions.cancel}
          </RegularButton>
          <RegularButton
            variant="contained"
            type="submit"
            color="info"
          >
            {isAddMode
              ? APARTMENTS_PHRASE.modal.actions.add
              : isDeleteMode
                ? APARTMENTS_PHRASE.modal.actions.delete
                : APARTMENTS_PHRASE.modal.actions.save}
          </RegularButton>
        </ButtonsWrapper>
      </StyledForm>
    </>
  );
});
